import React, { useState, useEffect } from 'react';
import { Form, Select, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getBlocks, getApartmentUnitsByBlock } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import type { Block, ApartmentUnit } from '@/types/resources/property';

interface BlockApartmentUnitSelectorProps {
  disabled?: boolean;
  onBlockDataChange?: (block: { id: string; name: string } | null) => void;
  onApartmentUnitDataChange?: (apartmentUnit: { id: string; name: string } | null) => void;
  fieldIds?: {
    blockId?: string;
    apartmentUnitId?: string;
  };
}

export const BlockApartmentUnitSelector: React.FC<BlockApartmentUnitSelectorProps> = ({
  disabled = false,
  onBlockDataChange,
  onApartmentUnitDataChange,
  fieldIds
}) => {
  const { t } = useTranslation(['properties', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  // Fetch blocks
  const { data: blocksData } = useQuery({
    queryKey: ['blocks', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getBlocks({
        searchTerm: '',
        pageSize: 1000,
        firestoreUser
      });
    },
    enabled: !!firestoreUser?.workspace?.id
  });

  // Fetch apartment units for selected block
  const { data: unitsData } = useQuery({
    queryKey: ['apartmentUnits', selectedBlockId],
    queryFn: () => {
      if (!firestoreUser || !selectedBlockId) throw new Error('User not authenticated or no block selected');
      return getApartmentUnitsByBlock(selectedBlockId, firestoreUser);
    },
    enabled: !!firestoreUser && !!selectedBlockId
  });

  // Initialize selectedBlockId from form values when component mounts or form values change
  useEffect(() => {
    const blockId = form.getFieldValue('blockId');
    if (blockId && blockId !== selectedBlockId) {
      setSelectedBlockId(blockId);
      
      // Provide block data to parent
      const block = blocksData?.blocks?.find((b: Block) => b.id === blockId);
      onBlockDataChange?.(block ? { id: block.id, name: block.name } : null);
    }
  }, [form, blocksData, selectedBlockId, onBlockDataChange]);

  // Handle apartment unit initialization when unitsData changes
  useEffect(() => {
    if (unitsData?.apartmentUnits) {
      const apartmentUnitId = form.getFieldValue('apartmentUnitId');
      if (apartmentUnitId) {
        const unit = unitsData.apartmentUnits.find((u: ApartmentUnit) => u.id === apartmentUnitId);
        onApartmentUnitDataChange?.(unit ? { id: unit.id, name: unit.number } : null);
      }
    }
  }, [unitsData, form, onApartmentUnitDataChange]);

  // Additional effect to handle form value changes after initial load
  useEffect(() => {
    const blockId = form.getFieldValue('blockId');
    if (blockId && !selectedBlockId) {
      setSelectedBlockId(blockId);
    }
  }, [form, selectedBlockId]);

  const handleBlockChange = (blockId: string) => {
    setSelectedBlockId(blockId);
    
    // Provide block data to parent
    if (blockId) {
      const block = blocksData?.blocks?.find((b: Block) => b.id === blockId);
      onBlockDataChange?.(block ? { id: block.id, name: block.name } : null);
    } else {
      onBlockDataChange?.(null);
    }
    
    // Reset apartment unit data when block changes
    onApartmentUnitDataChange?.(null);
  };

  const handleApartmentUnitChange = (apartmentUnitId: string) => {
    // Provide apartment unit data to parent
    if (apartmentUnitId) {
      const unit = unitsData?.apartmentUnits?.find((u: ApartmentUnit) => u.id === apartmentUnitId);
      onApartmentUnitDataChange?.(unit ? { id: unit.id, name: unit.number } : null);
    } else {
      onApartmentUnitDataChange?.(null);
    }
  };

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          name="blockId"
          label={t('properties:common.blockId')}
        >
          <Select
            id={fieldIds?.blockId}
            placeholder={t('properties:blocks.details.attributes.selectBlock')}
            onChange={handleBlockChange}
            disabled={disabled}
            allowClear
          >
            {blocksData?.blocks?.map((block: Block) => (
              <Select.Option key={block.id} value={block.id}>
                {block.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="apartmentUnitId"
          label={t('properties:common.unitId')}
        >
          <Select
            id={fieldIds?.apartmentUnitId}
            placeholder={t('properties:common.ui.select')}
            onChange={handleApartmentUnitChange}
            disabled={disabled || !selectedBlockId}
            allowClear
          >
            {unitsData?.apartmentUnits?.map((unit: ApartmentUnit) => (
              <Select.Option key={unit.id} value={unit.id}>
                {unit.number}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </Row>
  );
}; 